import * as React from 'react';

import { isEmpty, last, concat } from 'ramda';
import { EStepCode, TFormStoreData, TNextStep, TNextStepArguments } from '../../../types/form';
import {
  IStepsHistoryItem,
  TAnswersState,
  THandleFormSubmit,
  TQuestionMap,
  TSetAnswer,
  TStepsMap,
} from '../types/internal';
import { getNextStep } from './getNextStep';
import { getProgressBarData } from './getProgressBarData';
import { useQuizCompleteControls } from './useQuizCompleteControls';
import { useBackInterceptor } from './useBackIntercaptors';
import { getQuestionsMap } from './getQuestionsMap';
import { getStepsMap } from './getStepsMap';
import { prepareCurrentQuestion } from './prepareCurrentQuestion';
import { mapNextStepToHistoryItem } from './mapNextStepToHistoryItem';
import { getPrevStep } from './getPrevStep';
import { useDeviceType } from '@cian/ui-kit';
import { trackPrevStepEvent } from '../../../utils/useFormTracking/tracking';
import { trackStartQuizEvent } from '../tracking';

export const useFormController = (formData: TFormStoreData, handleSubmit: THandleFormSubmit) => {
  const deviceType = useDeviceType();
  const questionsMap = React.useMemo<TQuestionMap>(() => getQuestionsMap(formData), [formData]);
  const stepsMap = React.useMemo<TStepsMap>(() => getStepsMap(formData), [formData]);

  const [stepsHistory, setStepsHistory] = React.useState<IStepsHistoryItem[]>([
    { stepCode: formData.schema.firstStepCode, stepArgs: null },
  ]);
  const [currentStepCode, setCurrentStepCode] = React.useState<EStepCode>(formData.schema.firstStepCode);
  const [stepsToSkip, setStepsToSkip] = React.useState<EStepCode[]>([]);
  const [currentStepArgs, setCurrentStepArgs] = React.useState<TNextStepArguments | null | undefined>(null);
  const [answersState, setAnswersState] = React.useState<TAnswersState>({} as TAnswersState);
  const [quizStarted, setQuizStarted] = React.useState(false);
  const quizCompleteControls = useQuizCompleteControls(handleSubmit);
  const { backInterceptor, registerBackInterceptor, unRegisterBackInterceptor } = useBackInterceptor();

  /** Флаг, позволяющий следить, какой из переходов произошел последним - к следующему или предыдущему вопросу */
  const [isSetPrev, setPrev] = React.useState(false);

  /**
   * Объект шага находится из известного текущего кода шага,
   * а объект вопроса находится из questionCode в текущем шагу */
  const currentStep = stepsMap[currentStepCode];
  const currentQuestion = prepareCurrentQuestion({ currentStep, currentStepArgs, questionsMap });

  /**
   * _answersState специально передается в аргументе извне, т.к answersState не всегда успевает обновиться
   * это происходит, когда в компоненте подряд вызывается setAnswers, setNextStep (напр в Radio)
   * */
  const setNextStep = (_answersState: TAnswersState) => {
    const steps = getNextStep({
      currentStep,
      answersState: _answersState,
      stepsToSkip,
      formData,
    });

    if (!steps || isEmpty(steps)) {
      return;
    }

    const nextStep = last(steps) as TNextStep;

    setCurrentStepCode(nextStep.nextStepCode);
    setCurrentStepArgs(nextStep.nextStepArgs);
    setStepsHistory(prev => concat(prev, steps.map(mapNextStepToHistoryItem)));
    setPrev(false);
  };

  const setPrevStep = () => {
    if (backInterceptor) {
      const shouldPassThrough = backInterceptor();
      if (!shouldPassThrough) {
        return;
      }
    }

    const prevStep = getPrevStep({ stepsToSkip, stepsHistory });

    trackPrevStepEvent({ field: currentQuestion.answerField, answersState, deviceType });

    if (prevStep) {
      const { prevHistoryItem, slicedHistory } = prevStep;

      setAnswersState({
        ...answersState,
        [currentQuestion.answerField]: undefined,
      });
      setStepsHistory(slicedHistory);
      setCurrentStepCode(prevHistoryItem.stepCode);
      setCurrentStepArgs(prevHistoryItem.stepArgs);
      setPrev(true);
    }
  };

  const setAnswer = React.useCallback<TSetAnswer>(newAnswer => {
    setAnswersState(answersState => {
      const answerContainer = answersState[newAnswer.answerField] || {};

      return {
        ...answersState,
        [newAnswer.answerField]: {
          ...answerContainer,
          ...newAnswer,
        },
      };
    });
  }, []);

  const startQuiz = React.useCallback(() => {
    setQuizStarted(true);
    trackStartQuizEvent(deviceType);
  }, [deviceType]);

  const progressbarData = getProgressBarData({ formData, currentStep, stepsHistory });

  return {
    currentQuestion,
    setNextStep,
    setPrevStep,
    setAnswer,
    answersState,
    setAnswersState,
    quizStarted,
    startQuiz,
    isSetPrev,
    registerBackInterceptor,
    unRegisterBackInterceptor,
    ...progressbarData,
    ...quizCompleteControls,
    stepsToSkip,
    setStepsToSkip,
    questionsMap,
    stepsMap,
    stepsHistory,
  };
};
